import { z } from 'zod';
import { MaintenanceCategory } from '../maintenance.js';
import {
  ComplementStatus,
  DecisionChannel,
  DecisionValue,
  GarageDecisionChannel,
  Id,
  LineKind,
  Money,
  Quantity,
  ShortText,
  Timestamp,
  VatRate,
  amount,
} from './common.js';

/** Vague 6 : commentaire d'une demande de modification (1 à 2 000 caractères, mêmes règles de liens que les messages). */
export const DECISION_COMMENT_MAX = 2000;
export const DecisionComment = z.string().trim().min(1).max(DECISION_COMMENT_MAX);

/**
 * Règle du commentaire : obligatoire pour `change_requested`, interdit pour les autres réponses (aucun texte
 * libre n'accompagne un accord, un refus ou un report). Le nombre de liens est contrôlé par l'API (422).
 */
function commentRule(d: { decision: string; comment?: string | undefined }, ctx: z.RefinementCtx): void {
  if (d.decision === 'change_requested' && !d.comment) {
    ctx.addIssue({ code: 'custom', path: ['comment'], message: 'Précisez la modification demandée' });
  }
  if (d.decision !== 'change_requested' && d.comment !== undefined) {
    ctx.addIssue({ code: 'custom', path: ['comment'], message: 'Commentaire réservé à une demande de modification' });
  }
}

/**
 * Council n°2 (priorité 3, 08/10/2026) : e-mail des travaux complémentaires autonome. Priorité choisie par le
 * garage (« Sécurité », « À prévoir », « Confort »), délai d'immobilisation supplémentaire estimé (texte court,
 * ex. « + 1 jour ») et phrase du chef d'atelier (signée du nom du membre qui envoie). Figés dans la version.
 */
export const COMPLEMENT_PRIORITIES = ['safety', 'planned', 'comfort'] as const;
export const ComplementPriority = z.enum(COMPLEMENT_PRIORITIES);
export type ComplementPriority = z.infer<typeof ComplementPriority>;
export const COMPLEMENT_PRIORITY_LABELS: Record<ComplementPriority, string> = {
  safety: 'Sécurité',
  planned: 'À prévoir',
  comfort: 'Confort',
};
export const EXTRA_DOWNTIME_MAX = 40;
export const WORKSHOP_NOTE_MAX = 300;
/** Champs de présentation au client (saisie) : chaîne vide = effacé. */
const presentationInput = {
  priority: ComplementPriority.nullable().optional(),
  extraDowntime: z.string().trim().max(EXTRA_DOWNTIME_MAX).nullable().optional(),
  workshopNote: z.string().trim().max(WORKSHOP_NOTE_MAX).nullable().optional(),
};
/** Champs de présentation (lecture) ; facultatifs pour les maquettes existantes. */
const presentationOutput = {
  priority: ComplementPriority.nullable().optional(),
  extraDowntime: z.string().nullable().optional(),
  workshopNote: z.string().nullable().optional(),
};

/** Ligne de brouillon : prix facultatif tant que le complément n'est pas envoyé. */
export const ComplementLineInput = z.strictObject({
  /**
   * Identifiant d'une ligne existante (révision) : son prix est conservé si `unitPriceHt` est absent.
   * Absent pour une nouvelle ligne. Le prix suit la ligne, jamais sa position (audit C2).
   */
  id: Id.optional(),
  kind: LineKind,
  maintenanceCategory: MaintenanceCategory.nullable().optional(),
  label: ShortText,
  reference: ShortText.nullable().optional(),
  quantity: Quantity,
  unitPriceHt: Money.nullable().optional(),
  vatRate: VatRate,
});

export const ComplementLineOutput = z.object({
  /** Identifiant stable de la ligne (à renvoyer en révision). */
  id: Id,
  position: z.int(),
  kind: LineKind,
  maintenanceCategory: MaintenanceCategory.nullable(),
  label: z.string(),
  reference: z.string().nullable(),
  quantity: Quantity,
  unitPriceHt: amount(),
  vatRate: VatRate,
  totalHt: amount(),
  totalTtc: amount(),
});

export const ComplementTotals = z.object({ totalHt: amount(), totalVat: amount(), totalTtc: amount() });

export const DecisionOutput = z.object({
  id: Id,
  version: z.int(),
  decision: DecisionValue,
  channel: DecisionChannel,
  decidedAt: Timestamp,
  /** Membre du garage qui a saisi la décision (téléphone, sur place) ; `null` pour le portail. */
  recordedByName: z.string().nullable(),
  /**
   * Décision prise sur le portail avec un lien **partagé par un membre** (« Partager le lien ») :
   * nom de ce membre, affiché « via un lien partagé par … ». `null` pour un lien reçu par e-mail.
   */
  sharedByName: z.string().nullable(),
  note: z.string().nullable(),
  /** Vague 6 : commentaire du client pour une demande de modification, sinon `null` ; facultatif pendant la vague 6 (lots en cours) ; rendu obligatoire par L0 à l’intégration. */
  comment: z.string().nullable().optional(),
  totalTtc: amount(),
});

export const ComplementOutput = z.object({
  id: Id,
  interventionId: Id,
  title: z.string(),
  status: ComplementStatus,
  currentVersion: z.int(),
  lines: z.array(ComplementLineOutput),
  mediaIds: z.array(Id),
  totals: ComplementTotals,
  createdByName: z.string().nullable(),
  createdAt: Timestamp,
  sentAt: Timestamp.nullable(),
  decisions: z.array(DecisionOutput),
  ...presentationOutput,
});
export type ComplementOutput = z.infer<typeof ComplementOutput>;

export const ComplementInput = z.strictObject({
  title: ShortText,
  lines: z.array(ComplementLineInput).max(100),
  mediaIds: z.array(Id).max(50).default([]),
  ...presentationInput,
  /**
   * 08/10/2026 : prestation du catalogue du garage. Ses lignes (main-d'œuvre = temps × taux horaire,
   * pièces types) sont ajoutées **par le serveur** avant `lines`, prix compris, même sans `amounts.view`.
   * Prestation inconnue, retirée ou d'un autre garage : 422.
   */
  catalogItemId: Id.optional(),
});
export const ComplementUpdateInput = z.strictObject({
  title: ShortText.optional(),
  lines: z.array(ComplementLineInput).max(100).optional(),
  mediaIds: z.array(Id).max(50).optional(),
  ...presentationInput,
});

/** Décision transmise au garage (téléphone, sur place), liée à la version exacte. */
export const RecordDecisionInput = z
  .strictObject({
    version: z.int().positive(),
    decision: DecisionValue,
    channel: GarageDecisionChannel,
    note: ShortText.min(3),
    /** Vague 6 : modification demandée par le client (obligatoire pour `change_requested`). */
    comment: DecisionComment.optional(),
  })
  .superRefine(commentRule);

/** Décision du client sur le portail : idempotente par `idempotencyKey`. */
export const PortalDecisionInput = z
  .strictObject({
    version: z.int().positive(),
    decision: DecisionValue,
    idempotencyKey: Id,
    /** Vague 6 : modification demandée (obligatoire pour `change_requested`), reprise dans la conversation. */
    comment: DecisionComment.optional(),
  })
  .superRefine(commentRule);

/** Complément tel que vu par le client : version envoyée uniquement, jamais de brouillon. */
export const PortalComplementOutput = z.object({
  id: Id,
  title: z.string(),
  status: ComplementStatus,
  version: z.int(),
  lines: z.array(ComplementLineOutput),
  mediaIds: z.array(Id),
  totals: ComplementTotals,
  sentAt: Timestamp,
  decision: z
    .object({
      decision: DecisionValue,
      decidedAt: Timestamp,
      channel: DecisionChannel,
      /** Vague 6 : commentaire d'une demande de modification ; facultatif pendant la vague 6 (lots en cours) ; rendu obligatoire par L0 à l’intégration. */
      comment: z.string().nullable().optional(),
    })
    .nullable(),
  ...presentationOutput,
  /** Nom du membre qui a envoyé la version (signature de la phrase du chef d'atelier), sinon `null`. */
  signedBy: z.string().nullable().optional(),
  /** Vrai tant que le client peut encore accepter, refuser, reporter ou demander une modification de cette version. */
  decidable: z.boolean(),
});
